/**
 * TradingWorkflowDashboard Component
 *
 * Landing dashboard for Trading Workflow management with workflow phase cards.
 * Provides navigation to Pre-Trading, Live Trading, Post-Trading, and Objectives settings.
 */

// 1. External dependencies
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  TrendingUp,
  FileText,
  Target,
  Camera,
  ArrowRight
} from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody, CardFooter, Chip } from '@enfomentis/ui';

type WorkflowPhase = 'pre-trading' | 'live-trading' | 'post-trading' | 'objectives' | 'market-snapshots';

interface WorkflowCard {
  key: WorkflowPhase;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'available' | 'coming-soon';
  features: string[];
}

const workflowCards: WorkflowCard[] = [
  {
    key: 'pre-trading',
    title: 'Pre-Trading',
    description: 'Preparation and planning before trading',
    icon: <ClipboardCheck size={24} />,
    status: 'available',
    features: [
      'Personal preparation',
      'Trading objectives',
      'Economic calendar',
      'Pre-market analysis',
      'Final checks',
      'Transition screen'
    ]
  },
  {
    key: 'live-trading',
    title: 'Live Trading',
    description: 'Real-time trading execution and monitoring',
    icon: <TrendingUp size={24} />,
    status: 'coming-soon',
    features: [
      'Trade journal interface',
      'Quick action buttons',
      'Performance metrics',
      'Trade notes',
      'Opportunity tracking'
    ]
  },
  {
    key: 'post-trading',
    title: 'Post-Trading',
    description: 'Review and reflection after trading',
    icon: <FileText size={24} />,
    status: 'coming-soon',
    features: [
      'Daily review',
      'Performance analysis',
      'AI-powered insights',
      'Reflection prompts',
      'Trade export'
    ]
  },
  {
    key: 'objectives',
    title: 'Objectives',
    description: 'Manage your trading objectives library',
    icon: <Target size={24} />,
    status: 'available',
    features: [
      'Create objectives',
      'Queue for sessions',
      'Track progress'
    ]
  },
  {
    key: 'market-snapshots',
    title: 'Market Snapshots',
    description: 'Configure fields for capturing market conditions',
    icon: <Camera size={24} />,
    status: 'available',
    features: [
      'Custom field types',
      'Dropdown options',
      'Field ordering'
    ]
  }
];

/**
 * Trading Workflow Dashboard
 *
 * Provides card-based navigation to different workflow phases.
 * Uses progressive disclosure pattern - click card to access full feature.
 */
export const TradingWorkflowDashboard: React.FC = () => {
  const navigate = useNavigate();

  const handleCardPress = (phase: WorkflowPhase): void => {
    if (phase === 'pre-trading') {
      navigate('/settings/workflow/pre-trading');
    } else if (phase === 'objectives') {
      navigate('/settings/workflow/objectives');
    } else if (phase === 'market-snapshots') {
      navigate('/settings/workflow/market-snapshots');
    }
    // Future: handle other phases when implemented
  };

  return (
    <>
      {/* Feature Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {workflowCards.map((card) => (
          <Card
            key={card.key}
            isPressable={card.status === 'available'}
            isHoverable={card.status === 'available'}
            className={`h-full ${
              card.status === 'available'
                ? 'hover:scale-[1.02] transition-transform cursor-pointer'
                : 'opacity-75'
            }`}
            onPress={() => card.status === 'available' && handleCardPress(card.key)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between w-full">
                <div className="flex items-center gap-3">
                  <div className="text-primary">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {card.title}
                    </h3>
                    <p className="text-sm text-text-secondary mt-1">
                      {card.description}
                    </p>
                  </div>
                </div>
                <Chip
                  size="sm"
                  color={card.status === 'available' ? 'success' : 'default'}
                  variant="flat"
                >
                  {card.status === 'available' ? 'Available' : 'Coming Soon'}
                </Chip>
              </div>
            </CardHeader>

            <CardBody className="pt-3">
              <div className="space-y-2">
                <p className="text-sm font-medium text-text-primary">Features:</p>
                <ul className="space-y-1">
                  {card.features.map((feature, index) => (
                    <li
                      key={index}
                      className="text-sm text-text-secondary flex items-center gap-2"
                    >
                      <span className="text-primary">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </CardBody>

            {card.status === 'available' && (
              <CardFooter className="pt-3">
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  <span>Configure settings</span>
                  <ArrowRight size={16} />
                </div>
              </CardFooter>
            )}
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card className="bg-default-50">
        <CardBody className="p-6">
          <h4 className="text-md font-semibold text-text-primary mb-2">
            Trading Workflow Configuration
          </h4>
          <p className="text-sm text-text-secondary">
            Configure your complete trading workflow from preparation to review. Each phase contains
            specific settings and tools to support your trading process at different stages.
          </p>
        </CardBody>
      </Card>
    </>
  );
};
